import React, { FC, useEffect, useMemo, useRef } from 'react';
import Modal from 'components/shared/Modal';
import { PostDetail } from 'types/PostDetail';
import { usePage } from 'modules/page/pageSelector';
import TargetProfileForm from 'app/modules/posts/components/shiftPosts/TargetProfileForm';
import { Alert, Button, Icon, ActivityIndicator } from '@kontentino/ui';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { STEPS, useShiftPosts } from 'app/modules/posts/hooks/useShiftPosts';
import { useTranslation } from 'react-i18next';
import { useGetInitialTargetsToDuplicate } from 'app/modules/posts/modules/duplicatePosts/hooks/useGetInitialTargetsToDuplicate';
import { useQuery } from 'react-query';
import ContentPlanApi from 'app/api/contentPlan';
import { queryKey } from 'constants/queryKey';
import Footer from 'app/modules/posts/components/shiftPosts/Footer';
import { ApiClientError } from 'api/client';
import { useToast } from 'app/hooks/useToast';
import { useLocation } from 'react-router';
import routes from 'constants/routes';
import { useInvalidatePostsQueries } from 'app/modules/posts/hooks/useInvalidatePostsQueries';

type Props = {
  open: boolean;
  onClose: () => void;
  post: PostDetail;
};

const DuplicateSelectedPost: FC<Props> = ({ open, onClose, post }) => {
  const { t } = useTranslation();
  const { pages } = usePage();
  const hasSetInitialTargets = useRef(false);
  const toast = useToast();
  const { pathname } = useLocation();
  const invalidatePostsQueries = useInvalidatePostsQueries();
  const selectedPosts = useMemo(() => [post], [post]);

  const handleClose = () => {
    invalidatePostsQueries();
    onClose();
  };

  const isOnPostsPage = [
    routes.POSTS_CALENDAR,
    routes.POSTS_LIST,
    routes.POSTS_KANBAN,
    routes.POSTS_GRID,
  ].some((route) => pathname.startsWith(route));

  const pagesIds = pages.map((page) => page.id);

  const {
    data: pagesProjectsData,
    isLoading: isPagesProjectsLoading,
    isError: isPagesProjectsError,
  } = useQuery(
    queryKey.postsContentPlans(pagesIds),
    () => ContentPlanApi.getPagesContentPlans(pagesIds),
    {
      enabled: open && pagesIds.length > 0,
      onError: (e: ApiClientError) => {
        toast(e?.userMessage || t('somethingWentWrong'), 'error');
      },
    },
  );

  const isLoading = isPagesProjectsLoading;
  const isError = isPagesProjectsError;

  const container = useShiftPosts({
    initialStep: STEPS.SELECT_TARGET_PROFILES.step,
    initialPosts: selectedPosts,
    onFinish: handleClose,
  });

  const getInitialTargets = useGetInitialTargetsToDuplicate({
    initialPosts: selectedPosts,
    initialProjects: pagesProjectsData?.data,
    postsProjects: pagesProjectsData?.data,
  });
  const { setTargets } = container;

  useEffect(() => {
    hasSetInitialTargets.current = false;
  }, [post.id]);

  useEffect(() => {
    if (
      !hasSetInitialTargets.current &&
      pagesProjectsData?.data &&
      isOnPostsPage
    ) {
      const detectedInitialTargets = getInitialTargets();
      setTargets(detectedInitialTargets);
      hasSetInitialTargets.current = true;
    }
  }, [pagesProjectsData, getInitialTargets, setTargets, isOnPostsPage]);

  container.targetHandlers.current = {};

  return (
    <Modal open={open} onClose={handleClose} size="md">
      {isLoading ? (
        <div className="tw-flex tw-h-full  tw-w-full tw-justify-center tw-p-4">
          <ActivityIndicator className="tw-h-24  tw-w-24 tw-shrink-0" />
        </div>
      ) : (
        <>
          <Modal.Header title={t('duplicatePosts')} />
          <Modal.Content>
            {isError && (
              <Alert variant="danger" className="tw-mb-6">
                {t('somethingWentWrongTryAgain')}
              </Alert>
            )}
            {!isError && (
              <>
                <div className="tw-mb-2 tw-flex tw-flex-col tw-gap-y-4">
                  {container.targets.map((target, index) => (
                    <TargetProfileForm
                      key={target.id}
                      selectedPosts={selectedPosts}
                      dataNamePrefix="posts_duplicate-target"
                      availablePages={pages}
                      targetIndex={index + 1}
                      initialTarget={target}
                      ref={(ref) => {
                        if (ref) {
                          container.targetHandlers.current[target.id] = ref;
                        }
                      }}
                      removeTarget={
                        container.targets.length > 1
                          ? () => container.removeTarget(target.id)
                          : undefined
                      }
                      error={container.errors[target.id]}
                    />
                  ))}
                </div>
                {!container.isLastStep && (
                  <Button
                    variant="tertiary"
                    onClick={container.addTarget}
                    iconBefore={<Icon icon={faPlus} />}
                    data-name="post_duplicate-add-profile"
                    data-cy="post_duplicate-add-profile"
                  >
                    {t('addProfile')}
                  </Button>
                )}
              </>
            )}
          </Modal.Content>
          <Footer
            variant="duplicate"
            activeStep={container.step}
            canGoNext={container.canGoNext}
            onGoNext={container.goNext}
            onSubmit={container.onSubmit}
          />
        </>
      )}
    </Modal>
  );
};

export default DuplicateSelectedPost;
